import './table.scss';
import React, { ReactNode, useMemo } from 'react';
import { Column, CrudConfig, flattenColumns } from '../../../model/table';
import { EditorMode, TableMode } from 'src/utils/enums';
import { keyboardClickable } from 'src/utils/a11y-utils';
import { TableAction, TableRow } from '../table-row/table-row';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { NoResult } from '../no-result/no-result';
import Loader from '../loader/loader';
import Sort from '@svg/sort.svg?react';

interface TableProps<T> {
    mode?: TableMode,
    columnsConfig: Column<T>[];
    content: T[] | undefined;
    crudConfig?: CrudConfig<T>;
    isEmptyState?: boolean;
    emptyStateComp?: ReactNode;
    openRowToEdit?: (rowData: T, modalMode: EditorMode) => void;
    isInactive?: (rowData: T) => boolean;
    isDraggable?: (rowData: T) => boolean;
    actions?: TableAction<T>[];
    moveRow?: (dragIndex: number, hoverIndex: number) => void;
    isLoading?: boolean;
    sortConfig?: { column: keyof T | null, direction: 'asc' | 'desc' };
    onSort?: (column: keyof T) => void;
}

export const Table = <T, >({
                               columnsConfig, content, crudConfig, mode = TableMode.REGULAR,
                               isEmptyState, emptyStateComp, openRowToEdit, isInactive, isDraggable,
                               actions, moveRow, isLoading, sortConfig, onSort
                           }: TableProps<T>) => {

    const isSortable = (column: Column<T>) => !!onSort && column.isSortable;

    const isSpecialHeader = flattenColumns(columnsConfig).some(col => col.parent || col.sub);
    const columnContent = (column: Column<T>) => <div className="column-label-content">
        {isSpecialHeader ? <>
            <div className={`parent-header ${!column.parent ? 'empty-parent-header' : ''}`}>
                {column.parent || column.sub ? column.parent : column.header}
            </div>
            <div className={`header-content ${column.sub ? 'sub' : 'empty-sub-header'}`}>
                {column.sub ? column.header : ''}
            </div>
        </> :
            <div className={`header-content `}>
                {column.header}
                {sortConfig?.column === column.accessor && <Sort className={`sort-icon ${sortConfig.direction}`}/>}
            </div>
       }
    </div>;

    const flatColumns = useMemo(() => flattenColumns(columnsConfig), [columnsConfig]);

    const getProperDisplay = () => {
        if (isEmptyState) return <div className={'table-empty-state'}>{emptyStateComp}</div>;
        if (!content) return null;
        if (content?.length) return content.map((row, indexY) =>
                <TableRow<T> key={`row_${indexY}`}
                             columnsConfig={columnsConfig}
                             crudConfig={crudConfig}
                             data={row}
                             index={indexY}
                             openToEdit={openRowToEdit}
                             isInactive={isInactive}
                             isDraggable={isDraggable}
                             moveRow={moveRow}
                             actions={actions}
                             lastItemIndex={content.length - 1}
                />);
        return <NoResult/>;
    };

    return <DndProvider backend={HTML5Backend}>
        <div className={`table ${mode.toLowerCase()}`}>
            <div className={'table-header'}>
                <div className={'header-row'}>
                    {flatColumns.map((column, index) => <div key={`${index}-${column.accessor}`} data-testid={`column-label ${index}`}
                                                             style={{ minWidth: column.width, width: column.width }}
                                                             className={`column-label ${index} table-sticky-col-${column.sticky} ${column.accessor} ` +
                                                                 `${isSortable(column) ? 'sortable' : ''}`}
                                                             {...(isSortable(column) ? keyboardClickable(() => onSort?.(column.accessor as keyof T)) : {})}>
                            {columnContent(column)}

                        </div>
                    )}
                    <div className="column-label actions" >
                        {isSpecialHeader && <><div className={'parent-header'}/>
                            <div className={'sub'}/> </>}
                    </div>
                </div>
            </div>

            <div className={'table-content'} data-testid={'table-content'}>
                {(isLoading || content === undefined) && <Loader type="full-screen"/>}
                {getProperDisplay()}
            </div>
        </div>
    </DndProvider>;
};